<!-- Payment Method Container -->
<div class="payment-container">

  <!-- Loading Skeleton -->
  @if (isLoading) {
    <div class="payment-skeleton">
      <!-- Header Skeleton -->
      <div class="skeleton-header">
        <div class="skeleton-title"></div>
        <div class="skeleton-amount"></div>
      </div>

      <!-- Payment Methods Skeleton -->
      <div class="skeleton-methods">
        <div class="skeleton-method-title"></div>
        @for (item of [1,2,3,4]; track item) {
          <div class="skeleton-method-card">
            <div class="skeleton-method-icon"></div>
            <div class="skeleton-method-content">
              <div class="skeleton-method-name"></div>
              <div class="skeleton-method-desc"></div>
            </div>
            <div class="skeleton-method-arrow"></div>
          </div>
        }
      </div>
    </div>
  } @else {

    <!-- Back Button -->
    <div class="back-section">
      <button class="back-btn" (click)="goBack()">
        <i class="fa fa-arrow-left"></i>
        <span>Back to Order</span>
      </button>
    </div>

    <!-- Remaining Amount Section -->
    <div class="amount-header">
      <div class="amount-card">
        <div class="amount-icon">
          <i class="fa fa-credit-card"></i>
        </div>
        <div class="amount-content">
          <label class="amount-label">Remaining Amount</label>
          <div class="amount-value" [class.calculating]="isCalculatingAmount">
            @if (isCalculatingAmount) {
              <div class="calculating-spinner"></div>
            } @else {
              ₦{{ remainingAmount | number:'1.2-2' }}
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Methods Section -->
    <div class="payment-methods">
      <div class="methods-header">
        <h2 class="methods-title">
          <i class="fa fa-payment"></i>
          Choose Payment Method
        </h2>
        <p class="methods-subtitle">Select your preferred payment option</p>
      </div>

      <div class="methods-grid">
        <!-- General Transfer -->
        <div class="method-card"
             [class.selected]="selectedPaymentMethod === 'general_transfer'"
             [class.processing]="isProcessingPayment && selectedPaymentMethod === 'general_transfer'">
          <div class="method-main" (click)="selectGeneralTransferPayment()">
            <div class="method-icon">
              <img src="icons/bank-transfer.svg" alt="General Transfer">
            </div>
            <div class="method-content">
              <h3 class="method-name">General Transfer</h3>
              <p class="method-description">Quick bank transfer</p>
            </div>
            @if (!showGeneralTransferInput) {
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            }
          </div>

          @if (showGeneralTransferInput) {
            <div class="method-input-section">
              <form (ngSubmit)="initiatePayment('general_transfer', generalTransferAmount)"
                    class="payment-form">
                <div class="input-group">
                  <label class="input-label">Transfer Amount</label>
                  <div class="input-wrapper">
                    <span class="currency-symbol">₦</span>
                    <input
                      type="number"
                      class="amount-input"
                      placeholder="Enter transfer amount"
                      [(ngModel)]="generalTransferAmount"
                      name="generalTransferAmount"
                      [value]="remainingAmount"
                      required>
                  </div>
                </div>
                <button
                  type="submit"
                  class="confirm-btn"
                  [disabled]="isProcessingPayment">
                  @if (isProcessingPayment) {
                    <div class="btn-spinner"></div>
                    <span>Processing...</span>
                  } @else {
                    <i class="fa fa-check"></i>
                    <span>Confirm Transfer</span>
                  }
                </button>
              </form>
            </div>
          }
        </div>

        <!-- Cash Payment -->
        <div class="method-card"
             [class.selected]="selectedPaymentMethod === 'cash'"
             [class.processing]="isProcessingPayment && selectedPaymentMethod === 'cash'">
          <div class="method-main" (click)="selectCashPayment()">
            <div class="method-icon">
              <img src="icons/cash.svg" alt="Cash">
            </div>
            <div class="method-content">
              <h3 class="method-name">Cash Payment</h3>
              <p class="method-description">Pay with cash</p>
            </div>
            @if (!showCashInput) {
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            }
          </div>

          @if (showCashInput) {
            <div class="method-input-section">
              <form (ngSubmit)="initiatePayment('cash', cashAmount)"
                    class="payment-form">
                <div class="input-group">
                  <label class="input-label">Cash Amount</label>
                  <div class="input-wrapper">
                    <span class="currency-symbol">₦</span>
                    <input
                      type="number"
                      class="amount-input"
                      placeholder="Enter cash amount"
                      [(ngModel)]="cashAmount"
                      name="cashAmount"
                      [value]="remainingAmount"
                      required>
                  </div>
                </div>
                <button
                  type="submit"
                  class="confirm-btn"
                  [disabled]="isProcessingPayment">
                  @if (isProcessingPayment) {
                    <div class="btn-spinner"></div>
                    <span>Processing...</span>
                  } @else {
                    <i class="fa fa-money-bill"></i>
                    <span>Confirm Cash</span>
                  }
                </button>
              </form>
            </div>
          }
        </div>

        <!-- Dynamic Bank Transfer -->
        <div class="method-card"
             [class.selected]="selectedPaymentMethod === 'transfer'"
             [class.processing]="isProcessingPayment && selectedPaymentMethod === 'transfer'">
          <div class="method-main" (click)="selectTransferPayment()">
            <div class="method-icon">
              <img src="icons/bank-transfer.svg" alt="Dynamic Transfer">
            </div>
            <div class="method-content">
              <h3 class="method-name">Dynamic Bank Transfer</h3>
              <p class="method-description">Automated bank transfer</p>
            </div>
            @if (!showTransferInput) {
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            }
          </div>

          @if (showTransferInput) {
            <div class="method-input-section">
              <form (ngSubmit)="initiatePayment('transfer', transferAmount)"
                    class="payment-form">
                <div class="input-group">
                  <label class="input-label">Transfer Amount</label>
                  <div class="input-wrapper">
                    <span class="currency-symbol">₦</span>
                    <input
                      type="number"
                      class="amount-input"
                      placeholder="Enter transfer amount"
                      [(ngModel)]="transferAmount"
                      name="transferAmount"
                      [value]="remainingAmount"
                      required>
                  </div>
                </div>
                <button
                  type="submit"
                  class="confirm-btn"
                  [disabled]="isProcessingPayment">
                  @if (isProcessingPayment) {
                    <div class="btn-spinner"></div>
                    <span>Creating Account...</span>
                  } @else {
                    <i class="fa fa-bank"></i>
                    <span>Generate Account</span>
                  }
                </button>
              </form>
            </div>
          }
        </div>

        <!-- Card (POS) Payment -->
        <div class="method-card"
             [class.selected]="selectedPaymentMethod === 'pos'"
             [class.processing]="isProcessingPayment && selectedPaymentMethod === 'pos'">
          <div class="method-main" (click)="selectPosPayment()">
            <div class="method-icon">
              <img src="icons/card.svg" alt="Card Payment">
            </div>
            <div class="method-content">
              <h3 class="method-name">Card Payment (POS)</h3>
              <p class="method-description">Credit/Debit card payment</p>
            </div>
            @if (!showPosInput) {
              <div class="method-arrow">
                <i class="fa fa-chevron-right"></i>
              </div>
            }
          </div>

          @if (showPosInput) {
            <div class="method-input-section">
              <form (ngSubmit)="initiatePayment('pos', posAmount)"
                    class="payment-form">
                <div class="input-group">
                  <label class="input-label">Card Amount</label>
                  <div class="input-wrapper">
                    <span class="currency-symbol">₦</span>
                    <input
                      type="number"
                      class="amount-input"
                      placeholder="Enter POS amount"
                      [(ngModel)]="posAmount"
                      name="posAmount"
                      [value]="remainingAmount"
                      required>
                  </div>
                </div>
                <button
                  type="submit"
                  class="confirm-btn pos-btn"
                  [disabled]="isProcessingPayment || isInitializingPOS">
                  @if (isProcessingPayment || isInitializingPOS) {
                    <div class="btn-spinner"></div>
                    <span>Initializing POS...</span>
                  } @else {
                    <i class="fa fa-credit-card"></i>
                    <span>Initialize POS</span>
                  }
                </button>
              </form>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>
